import React from "react";

export default function SeoGuideSection() {
  return (
    <section className="stf-section">
      <div className="stf-wrap">
        <div className="stf-seo-grid">
          <article className="stf-article">
            <div className="stf-kicker">Guide</div>
            <h2>Hiring corporate and office security in Australia: what to know before you book</h2>
            <p>
              Office and workplace security carries the same legal requirement as any other type of security work in Australia. Whether you're covering a single reception desk or running patrols across a multi site campus, the rules around who can work on your property and what they're allowed to do are set by your state, not by the building manager and not by the security company quoting you.
            </p>
            <p>
              This guide covers what businesses most often get wrong when hiring office and workplace security, and what to check before you confirm anyone.
            </p>

            <h3 id="licence">Do you legally need a licensed security officer?</h3>
            <p>
              Yes, in every state. Reception coverage, access control, patrols and after hours lockup all count as licensable security work. A general labour hire arrangement isn't enough on its own; the individual doing the work needs their own license.
            </p>
            <table>
              <thead>
                <tr>
                  <th>State</th>
                  <th>License commonly required</th>
                  <th>Regulator</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>New South Wales</td>
                  <td>Security License Class 1A, Security Officer</td>
                  <td>NSW Police SLED</td>
                </tr>
                <tr>
                  <td>Victoria</td>
                  <td>Private Security Individual Operator License, security guard activity</td>
                  <td>Victoria Police Licensing and Regulation Division</td>
                </tr>
                <tr>
                  <td>Queensland</td>
                  <td>Security Provider License Class 1, unarmed security officer</td>
                  <td>Office of Fair Trading</td>
                </tr>
                <tr>
                  <td>Western Australia</td>
                  <td>Security Officer license</td>
                  <td>WA Police Licensing</td>
                </tr>
                <tr>
                  <td>South Australia</td>
                  <td>Security Agents License, general guarding function</td>
                  <td>Consumer and Business Services</td>
                </tr>
              </tbody>
            </table>
            <div className="stf-callout">
              <b>Worth knowing:</b> in most states the same core license covers both static guarding and crowd control, but the class name and conditions still vary by state. Confirm current requirements with your state regulator before you finalise a roster.
            </div>

            <h3 id="how-many">How many security officers does your site need?</h3>
            <p>
              There's no single national ratio for office and workplace security. Most businesses work from the shape of the site and adjust for risk.
            </p>
            <ul>
              <li><strong>Floor plan and entry points:</strong> every door, loading dock and lift lobby that needs monitoring is its own position.</li>
              <li><strong>Hours of operation:</strong> business hours only coverage looks very different to 24/7 coverage.</li>
              <li><strong>Reception &amp; concierge:</strong> a staffed front desk changes the headcount and the skill set you're hiring for.</li>
              <li><strong>Risk profile:</strong> cash on site, high value stock or public access all raise the bar.</li>
              <li><strong>Shift handovers:</strong> continuous coverage needs overlap built into the roster, not just back to back shifts.</li>
            </ul>
            <p>If your building manager or insurer has already set a minimum, treat that as the floor, not the target.</p>

            <h3 id="cost">What corporate &amp; office security costs</h3>
            <p>
              Rates for licensed security officers in Australia generally sit between $34 and $48 an hour, depending on the work. The biggest factors are the time of day, whether it's a weekday or weekend, and whether you're booking a one off callout or an ongoing contract. Overnight and public holiday shifts sit at the top of that range.
            </p>
            <p>
              The other variable is who you book through. Agencies typically add a margin on top of the guard's rate for coordination and rostering. On Staffoo, guards set their own rate and you see it on their application, so the number you compare is the number you pay. You can see current averages in the pricing section above.
            </p>

            <h3 id="vs">Security officer or crowd controller, which do you need?</h3>
            <p>
              These are different licensed roles covering different work, and booking the wrong one can leave you not compliant on the day.
            </p>
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Security Officer</th>
                  <th>Crowd Controller</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Typical work</td>
                  <td>Covers static site guarding, patrols and access control.</td>
                  <td>Covers door work, entry screening and crowd monitoring.</td>
                </tr>
                <tr>
                  <td>Best for</td>
                  <td>Suits offices, warehouses, retail and construction sites.</td>
                  <td>Suits festivals, venues and licensed events.</td>
                </tr>
                <tr>
                  <td>Alcohol involved</td>
                  <td>Security Officer work doesn't usually involve alcohol service areas.</td>
                  <td>Crowd Controller work usually does, often with RSA training required.</td>
                </tr>
              </tbody>
            </table>

            <h3 id="checklist">What to check before you confirm a guard</h3>
            <ul>
              <li><strong>License class and expiry:</strong> that it matches the work and is current in your state.</li>
              <li><strong>Relevant experience:</strong> office and workplace experience is a different job to retail or event work.</li>
              <li><strong>Reviews from similar sites:</strong> volume matters less than whether the sites looked like yours.</li>
              <li><strong>Availability across the full roster:</strong> not just one shift, especially for ongoing contracts.</li>
              <li><strong>Who's responsible for insurance:</strong> and what happens if a guard can't make a shift.</li>
            </ul>
            <p>
              On Staffoo, license verification happens before a guard can apply, and the license class, experience and review history sit on every application you receive.
            </p>

            <h3 id="direct">Hiring direct instead of through an agency</h3>
            <p>
              Traditional agencies take your brief, roster guards internally and send you an invoice. You rarely know who's coming until the day, and you can't request the same person twice. Hiring direct through a platform flips that: you see who applied, you pick, and you can rebook the guards who worked out well. For businesses running an ongoing contract, that continuity usually matters more than the hourly rate.
            </p>
            <p>
              If you're hiring for a different setting, the same process applies to event security, retail security and construction site security.
            </p>

            <div className="stf-recap">
              <div className="stf-kicker">Before you post</div>
              <p>
                Confirm the license class your state requires, size your roster from entry points and hours rather than a guess, and treat agency quotes as a ceiling. Staffoo shows you each guard's real rate before you confirm anyone.
              </p>
            </div>
          </article>

          {/* Sidebar Cards Column */}
          <aside className="stf-seo-aside">
            <div className="stf-toc">
              <h4>On this page</h4>
              <a href="#license">Do you need a licensed guard?</a>
              <a href="#how-many">How many guards you need</a>
              <a href="#cost">What it costs</a>
              <a href="#vs">Security officer vs crowd controller</a>
              <a href="#checklist">Pre booking checklist</a>
              <a href="#direct">Direct vs agency</a>
            </div>

            <div className="stf-side-cta">
              <h4>Know what you need?</h4>
              <p>Post the job free and start receiving applications from licensed security officers near you.</p>
              <a href="#" className="stf-btn stf-btn-solid stf-btn-block">Post a corporate security job</a>
            </div>

            <div className="stf-aside-module">
              <h4>Related guides</h4>
              <a href="#">Licensing explained by state</a>
              <a href="#">How pricing works</a>
              <a href="#">Hiring for a business or agency</a>
              <a href="#">Event &amp; crowd control security</a>
            </div>

            <div className="stf-aside-trust">
              <div className="stf-aside-trust-row">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0A7C6E" strokeWidth="2">
                  <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Every applicant's license is checked before you see their profile.
              </div>
              <div className="stf-aside-trust-row">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0A7C6E" strokeWidth="2">
                  <path d="M12 2v20M17 6H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"></path>
                </svg>
                Free to post, no lock in contract, pay after sign off.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
